import React, { useEffect, useRef } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import clsx from 'clsx';
import { ChartSettings, ModeTheme } from '@moderepo/mode-app-components';
import { useTheme } from '@material-ui/core';



// Create a value (Y) axes/series for the chart
const createAxisAndSeries = (
    chart: am4charts.XYChart,
    dataFieldSettings: WaffleChartProps['dataFieldSettings'],
    rowCategories: readonly string[] | undefined,
    colCategories: readonly string[] | undefined,
    seriesName: string | undefined,
    theme: ModeTheme,
) => {

    const { rowField, colField, valueField, colorField } = dataFieldSettings;


    const rowAxis = chart.yAxes.push(new am4charts.CategoryAxis());
    rowAxis.dataFields.category = rowField;
    rowAxis.renderer.grid.template.disabled = true;
    rowAxis.renderer.inversed = true;
    rowAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
    if (rowCategories) {
        rowAxis.data = rowCategories.map((cat) => {
            return {
                [rowField]: cat,
            };
        });
    }

    const colAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    colAxis.dataFields.category = colField;
    colAxis.renderer.grid.template.disabled = true;
    colAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
    if (colCategories) {
        colAxis.data = colCategories.map((cat) => {
            return {
                [colField]: cat,
            };
        });
    }


    const series = chart.series.push(new am4charts.ColumnSeries());
    series.dataFields.categoryX = colField;
    series.dataFields.categoryY = rowField;
    series.dataFields.value = valueField;
    series.name = seriesName || valueField;
    series.sequencedInterpolation = true;
    series.defaultState.transitionDuration = 3000;


    // Set up cell appearance
    const column = series.columns.template;
    column.strokeWidth = 1;
    column.strokeOpacity = 0.5;
    column.stroke = am4core.color('#ffffff');
    column.tooltipText = `{${valueField}}`;

    series.columns.template.adapter.add('tooltipText', (
        tooltipText,
        target,
    ) => {
        const data = target.tooltipDataItem.dataContext as WaffleChartDataPoint;
        if (data) {
            // if tooltip is provided, return the tooltip value as provided in the data
            if (data.tooltip) {
                return data.tooltip;
            }

            // If tooltip is not provided, create a tooltip by combining the xAxis, yAxes, and value
            const value = data[valueField];
            if (typeof data[valueField] === 'number') {
                return `${data[rowField]}, ${data[colField]}: ${value}`;
            }
            return `${data[rowField]}, ${data[colField]}: N/A}`;
        }
        return tooltipText;
    });

    column.width = am4core.percent(100);
    column.height = am4core.percent(100);
    if (colorField) {
        column.propertyFields.fill = colorField;
    }
};


/**
 * This is only the BASE interface for the Waffle Chart's data points. The user can use ANY data object that extends this interface.
 * However, the user will need to configure the `dataFieldSettings` to tell the chart how to get data from the data object.
 */
export interface WaffleChartDataPoint {
    readonly tooltip?: string | undefined;
}


export interface WaffleChartProps extends ChartSettings {
    readonly chartId: string;
    readonly dataFieldSettings: {
        readonly rowField: string;                       // The field name from the data object where row value is stored
        readonly colField: string;                       // The field name from the data object where col value is stored
        readonly valueField: string;                     // The field name from the data object where the data value is stored
        readonly colorField?: string | undefined;        // The field name from the data object where the color value is stored
    };
    readonly seriesName?: string                         // The user friendly name to be used when displaying the name of the series
    readonly rowCategories?: readonly string[] | undefined;
    readonly colCategories?: readonly string[] | undefined;
    readonly dataPoints: readonly WaffleChartDataPoint[];
    readonly showLegends?: boolean;                         // default is true
    readonly disableZoom?: boolean;
}



export const WaffleChart: React.FC<WaffleChartProps> = (props: WaffleChartProps) => {

    const chartRef = useRef<am4charts.XYChart>();
    const theme = useTheme() as ModeTheme;


    // For debugging chart re-rendering
    const chartIdRef = useRef<string>();
    const showLegendsRef = useRef<boolean>();
    const disableZoomRef = useRef<boolean | undefined>();
    const dataPointsRef = useRef<readonly WaffleChartDataPoint[]>();
    const dataFieldSettingsRef = useRef<WaffleChartProps['dataFieldSettings']>();
    const rowCategoriesRef = useRef<readonly string[] | undefined>();
    const colCategoriesRef = useRef<readonly string[] | undefined>();
    const exportSettingsRef = useRef<ChartSettings['exportSettings']>();


    // This useEffect is used for cleaning up Chart when component unmounted
    useEffect(() => {
        // Return cleanup function
        return () => {
            const chart = chartRef.current as am4charts.XYChart;
            if (chart && !chart.isDisposed()) {
                // console.log('dispose chart');

                // dispose chart
                chart.dispose();
                chartRef.current = undefined;
            }
        };

    }, []);



    /**
     * This useEffect is used for creating/updating the chart. This useEffect depends on a lot of props and when each props change, this useEffect
     * will be called. So, it will be very slow if we were to re-create the chart every time this useEffect is called. We can't remove any of the
     * dependencies because the chart needs all the dependencies. So to solve this problem, we will keep a reference of the PREVIOUS prop values.
     * Each time this useEffect is called, we will compare the new prop values with the previous to determine which props changed. Some props
     * will always require a new chart to be created e.g. chartId and displayedSeries when they are changed. Some props does not which mean we can
     * just update the chart's properties when component props changed.
     */
    useEffect(() => {

        let chart = chartRef.current as am4charts.XYChart;

        // We will need to re-create chart if chartId changed or displaySeries changed
        const createNewChart: boolean = chartIdRef.current !== props.chartId || dataPointsRef.current !== props.dataPoints
            || dataFieldSettingsRef.current !== props.dataFieldSettings || rowCategoriesRef.current !== props.rowCategories
            || colCategoriesRef.current !== props.colCategories;


        if (!chart || createNewChart) {

            if (chart && !chart.isDisposed()) {
                // Dispose old chart first
                // console.log('dispose chart');

                // dispose chart
                chart.dispose();
                chartRef.current = undefined;
            }

            // create new Chart
            // console.log('create chart', props.chartId);
            chart = am4core.create(props.chartId, am4charts.XYChart);
            chart.plotContainer.background.stroke = am4core.color(theme.palette.chart.gridStroke);
            chartRef.current = chart;
        }


        if (createNewChart || exportSettingsRef.current !== props.exportSettings) {
            // exportSettings changed, update the chart.exportingMenu
            if (props.exportSettings) {
                if (!chart.exporting.menu) {
                    chart.exporting.menu = new am4core.ExportMenu();
                }
                if (props.exportSettings.align) {
                    chart.exporting.menu.align = props.exportSettings.align;
                }
                if (props.exportSettings.verticalAlign) {
                    chart.exporting.menu.verticalAlign = props.exportSettings.verticalAlign;
                }
                if (props.exportSettings.items) {
                    chart.exporting.menu.items = props.exportSettings.items;
                }
            } else if (!props.exportSettings && chart.exporting.menu) {
                chart.exporting.menu?.dispose();
            }
        }


        // if we just created a new chart or if the set of metric to be displayed changed or data field settings changed, recreate metric
        if (createNewChart || dataPointsRef.current !== props.dataPoints || dataFieldSettingsRef.current !== props.dataFieldSettings
            || rowCategoriesRef.current !== props.rowCategories || colCategoriesRef.current !== props.colCategories) {

            // remove the current series and yAxes first
            while (chart.series.length > 0) {
                chart.series.removeIndex(0).dispose();
            }

            while (chart.xAxes.length > 0) {
                chart.xAxes.removeIndex(0).dispose();
            }

            while (chart.yAxes.length > 0) {
                chart.yAxes.removeIndex(0).dispose();
            }

            // Create value axes and series for each metrics
            createAxisAndSeries(
                chart,
                props.dataFieldSettings,
                props.rowCategories,
                props.colCategories,
                props.seriesName,
                theme,
            );
        }


        // if we just created a new chart or if show legends option changed, update legends visibility
        if (createNewChart || showLegendsRef.current !== props.showLegends) {
            if (props.showLegends && !chart.legend) {
                // create legend if there isn't one
                chart.legend = new am4charts.Legend();
                chart.legend.labels.template.fill = am4core.color(theme.palette.text.primary);
            } else if (!props.showLegends && chart.legend) {
                // dispose legend if there is one
                chart.legend.dispose();
            }
        }


        // if we just created a new chart or the data changed, replace chart's data with new data
        if (createNewChart || dataPointsRef.current !== props.dataPoints) {
            chart.data = [...props.dataPoints];
        }


        chartIdRef.current = props.chartId;
        showLegendsRef.current = props.showLegends || false;
        disableZoomRef.current = props.disableZoom;
        dataPointsRef.current = props.dataPoints;
        dataFieldSettingsRef.current = props.dataFieldSettings;
        rowCategoriesRef.current = props.rowCategories;
        colCategoriesRef.current = props.colCategories;
        exportSettingsRef.current = props.exportSettings;
    }, [props.chartId, props.dataPoints, props.showLegends, props.disableZoom, props.rowCategories, props.colCategories, props.dataFieldSettings,
        props.seriesName, props.exportSettings, theme]);



    return (
        <div id={props.chartId} className={clsx(props.className)} />
    );
};
